/**
 * The full-screen media viewer opened from a `Media` tab tile: arrow keys,
 * on-screen buttons, and a horizontal touch swipe all step through every
 * resolved item without bouncing back to the panel between images. All three
 * paths share one navigation function so the clamp-at-both-ends behavior
 * cannot drift between them.
 *
 * Its open/closed state lives in `MediaTab`'s own component state, not a URL
 * param. That is the one deliberate exception to `url-state` in this card:
 * the viewer is a transient lightbox inside an overlay that is already URL
 * addressed (`?sheet=chat-details&panel=media`), and a third overlay key
 * would make one back press ambiguous between closing the viewer and closing
 * the panel. Documented here so a later reader does not read it as an
 * oversight.
 */
import { REDUCED_MOTION_SAFE } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef } from "react";

export type typeViewerItem = {
  messageId: string;
  largeUrl: string;
  sentAtIso: string;
};

export type typeMediaViewerProps = {
  items: readonly typeViewerItem[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
};

/** A horizontal drag past this many pixels counts as a swipe, not a tap. */
const SWIPE_THRESHOLD_PX = 40;

export const MediaViewer = (props: typeMediaViewerProps): ReactNode => {
  const { items, index, onIndexChange, onClose } = props;
  const current = items[index];
  const touchStartXRef = useRef<number | null>(null);

  const goToPrevious = (): void => {
    if (index > 0) onIndexChange(index - 1);
  };
  const goToNext = (): void => {
    if (index < items.length - 1) onIndexChange(index + 1);
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") {
        goToPrevious();
        return;
      }
      if (event.key === "ArrowRight") {
        goToNext();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [goToPrevious, goToNext, onClose]);

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>): void => {
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (event: React.TouchEvent<HTMLDivElement>): void => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX === null) return;

    const endX = event.changedTouches[0]?.clientX;
    if (endX === undefined) return;

    const deltaX = endX - startX;
    if (deltaX <= -SWIPE_THRESHOLD_PX) {
      goToNext();
    } else if (deltaX >= SWIPE_THRESHOLD_PX) {
      goToPrevious();
    }
  };

  if (!current) return null;

  return (
    <div
      data-testid="media-viewer"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        type="button"
        data-testid="media-viewer-close"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-4 top-4 min-h-touch-target min-w-touch-target text-body-strong text-white"
      >
        Close
      </button>
      <button
        type="button"
        data-testid="media-viewer-prev"
        aria-label="Previous"
        disabled={index === 0}
        onClick={goToPrevious}
        className={`absolute left-4 min-h-touch-target min-w-touch-target text-body-strong text-white disabled:opacity-30 ${REDUCED_MOTION_SAFE}`}
      >
        Previous
      </button>
      <img
        src={current.largeUrl}
        alt=""
        data-testid="media-viewer-image"
        className="max-h-full max-w-full object-contain"
      />
      <button
        type="button"
        data-testid="media-viewer-next"
        aria-label="Next"
        disabled={index === items.length - 1}
        onClick={goToNext}
        className={`absolute right-4 min-h-touch-target min-w-touch-target text-body-strong text-white disabled:opacity-30 ${REDUCED_MOTION_SAFE}`}
      >
        Next
      </button>
    </div>
  );
};
